// src/components/LearnMorePage.js

import React, { useState } from 'react';
import sinterklaasIllustration from '../assets/sinterklaas-illustration.png';
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PeopleIcon from '@mui/icons-material/People';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SyncIcon from '@mui/icons-material/Sync';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LinkIcon from '@mui/icons-material/Link';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// ─── Tab Panel ────────────────────────────────────────────────────────────────
function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 5 }}>
      {value === index && children}
    </Box>
  );
}

// ─── Comparison cell ──────────────────────────────────────────────────────────
function CompCell({ v }) {
  if (v === true) return <CheckIcon sx={{ color: '#2D6A4F', fontSize: 22 }} />;
  if (v === false) return <CloseIcon sx={{ color: '#C9C0B3', fontSize: 22 }} />;
  return <RemoveIcon sx={{ color: '#C9C0B3', fontSize: 22 }} />;
}

// ─── Structured data ──────────────────────────────────────────────────────────
const structuredData = {
  '@context': 'http://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      headline: 'Free Gift List & Secret Santa App — ListLink Features',
      description:
        'Organize wish lists, Secret Santa draws, Sinterklaas exchanges, and shared grocery lists with expense tracking. 100% free.',
      url: 'https://list-link.com/learn-more',
      datePublished: '2023-10-01',
      dateModified: '2026-02-23',
      author: { '@type': 'Organization', name: 'ListLink' },
      publisher: {
        '@type': 'Organization',
        name: 'ListLink',
        logo: { '@type': 'ImageObject', url: 'https://list-link.com/assets/logo.png' },
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is ListLink free forever?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. ListLink is completely free with no hidden fees, no premium tier, and no credit card required. All features — gift lists, Secret Santa events, expense tracking, and shared lists — are available at no cost.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does ListLink work for Sinterklaas?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. ListLink is the only international list app with dedicated Sinterklaas event support. You can organize Surprise exchanges, manage wish lists, and coordinate gifts for the Dutch December 5th tradition.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does the expense tracking work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "ListLink's expense dashboard lets you record purchases and track what you've spent per person and event. It shows your spending breakdown at a glance, eliminating the need for spreadsheets or separate budgeting apps.",
          },
        },
        {
          '@type': 'Question',
          name: 'How do I share a list with friends or family?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Invite others by email to join an event. Once they have an account, they can view all lists shared with that event and mark items as purchased.',
          },
        },
        {
          '@type': 'Question',
          name: 'How is ListLink different from Elfster or Amazon Wish Lists?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ListLink is the only app that combines gift wish lists, Secret Santa event management, Sinterklaas support, group expense tracking, and shared general lists (grocery, packing, to-do) in a single free app. Elfster focuses only on Secret Santa with no expense tracking or general lists. Amazon Wish Lists are Amazon-only with no event management or expense tracking.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do secret purchases work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'When you mark an item as secretly buying, only you can see that you\'ve claimed it. Other participants see it\'s taken — but the list owner never sees it was purchased, so the surprise is preserved.',
          },
        },
      ],
    },
  ],
};

// ─── Tab content definitions ──────────────────────────────────────────────────
const tabs = [
  { label: 'Gift Lists', icon: <CardGiftcardIcon sx={{ fontSize: 18 }} /> },
  { label: 'Secret Santa', icon: <PeopleIcon sx={{ fontSize: 18 }} /> },
  { label: 'Sinterklaas', icon: <CelebrationIcon sx={{ fontSize: 18 }} /> },
  { label: 'Expense Tracking', icon: <ReceiptLongIcon sx={{ fontSize: 18 }} /> },
  { label: 'General Lists', icon: <ListAltIcon sx={{ fontSize: 18 }} /> },
];

// ─── Extended comparison rows ─────────────────────────────────────────────────
const comparisonRows = [
  { feature: 'Gift wish lists', listlink: true, elfster: true, amazon: true },
  { feature: 'Secret Santa event management', listlink: true, elfster: true, amazon: false },
  { feature: 'Sinterklaas support', listlink: true, elfster: false, amazon: false },
  { feature: 'Secret purchase marking', listlink: true, elfster: true, amazon: false },
  { feature: 'Group expense tracking', listlink: true, elfster: false, amazon: false },
  { feature: 'Grocery / packing / to-do lists', listlink: true, elfster: false, amazon: false },
  { feature: 'Easy email invites to share lists', listlink: true, elfster: true, amazon: false },
  { feature: '100% free — all features', listlink: true, elfster: true, amazon: true },
  { feature: 'Works with any online store', listlink: true, elfster: 'partial', amazon: false },
];

// ─── Feature grid items ───────────────────────────────────────────────────────
const featureGrid = [
  { icon: SyncIcon, label: 'Syncs across devices', desc: 'Your lists stay up to date across all your devices' },
  { icon: PhoneAndroidIcon, label: 'Mobile-friendly', desc: 'Works great on any phone or tablet' },
  { icon: LinkIcon, label: 'Link sharing', desc: 'Share any list with a single URL' },
  { icon: PersonOffIcon, label: 'Email invites', desc: 'Invite collaborators by email to join events' },
  { icon: BookmarkIcon, label: 'Item reservations', desc: 'Claim items to prevent duplicates' },
  { icon: LockIcon, label: 'Privacy controls', desc: 'Choose who can see each list' },
  { icon: ContentCopyIcon, label: 'List duplication', desc: 'Copy a list to reuse it next year' },
  { icon: LinkIcon, label: 'Item links', desc: 'Add links from any online store to list items' },
  { icon: PeopleIcon, label: 'Group events', desc: 'Unlimited participants in any exchange' },
  { icon: CelebrationIcon, label: 'White Elephant', desc: 'Organize Yankee Swap / White Elephant events' },
  { icon: ReceiptLongIcon, label: 'Expense tracking', desc: 'Track what you spend per person and event' },
];

// ─── FAQ items ────────────────────────────────────────────────────────────────
const faqs = [
  {
    q: 'Is ListLink free forever?',
    a: 'Yes. ListLink is completely free — no premium tier, no hidden fees, no credit card required. Every feature is available to every user at no cost.',
  },
  {
    q: 'Does ListLink work for Sinterklaas?',
    a: 'Yes. ListLink is the only international list app with dedicated Sinterklaas event support. Organize Surprise exchanges, manage wish lists, and coordinate gifts for the Dutch December 5th tradition.',
  },
  {
    q: 'How does the expense tracking work?',
    a: "Record purchases as you go. ListLink's dashboard shows your spending broken down by person and event — no spreadsheets needed.",
  },
  {
    q: 'How do I share a list with friends or family?',
    a: 'Invite others by email to join an event. Once they have an account, they can view all lists shared with that event and mark items as purchased.',
  },
  {
    q: 'How do secret purchases work?',
    a: "Mark any item as secretly buying. Other participants see it's taken — but the list owner never sees it was purchased, keeping the surprise intact.",
  },
  {
    q: 'How is ListLink different from Elfster or Amazon Wish Lists?',
    a: 'ListLink uniquely combines gift lists, Secret Santa events, Sinterklaas support, group expense tracking, and general shared lists in one free app. Elfster focuses on Secret Santa only (no expense tracking, no general lists). Amazon Wish Lists are Amazon-only with no events or expense tracking.',
  },
];

// ─── Section heading helper ───────────────────────────────────────────────────
function SectionHeading({ overline, title, subtitle }) {
  return (
    <Box sx={{ textAlign: 'center', mb: 6 }}>
      {overline && (
        <Typography variant="overline" sx={{ color: '#E8891A', display: 'block', mb: 1 }}>
          {overline}
        </Typography>
      )}
      <Typography variant="h2" sx={{ mb: subtitle ? 1.5 : 0, fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: '#5C4F3D', maxWidth: 480, mx: 'auto', fontSize: '1rem' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// ─── Amber CTA section (reused at bottom) ─────────────────────────────────────
function AmberCTA({ title, sub, btnLabel, to }) {
  return (
    <Box
      sx={{
        py: { xs: 10, md: 12 },
        background: 'linear-gradient(135deg, #E8891A 0%, #C46F0C 100%)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', top: -80, left: -80, width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: -80, right: -80, width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
      <Container maxWidth="sm" sx={{ position: 'relative' }}>
        <Typography variant="h2" sx={{ color: '#fff', mb: 2, fontSize: { xs: '2rem', md: '2.6rem' }, fontStyle: 'italic' }}>
          {title}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 5, fontSize: '1rem' }}>
          {sub}
        </Typography>
        <Button
          variant="contained"
          size="large"
          component={Link}
          to={to}
          sx={{
            background: '#FFFFFF',
            color: '#E8891A',
            fontWeight: 800,
            px: 5,
            py: 1.75,
            fontSize: '1rem',
            '&:hover': {
              background: '#FDFAF5',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(26,20,16,0.25)',
            },
          }}
        >
          {btnLabel}
        </Button>
      </Container>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function LearnMorePage() {
  const [tabValue, setTabValue] = useState(0);

  return (
    <Box sx={{ background: '#FDFAF5', overflowX: 'hidden' }}>
      <Helmet>
        <title>Free Gift List &amp; Secret Santa App | ListLink</title>
        <meta
          name="description"
          content="Organize wish lists, Secret Santa draws, Sinterklaas exchanges, and shared grocery lists — with expense tracking. 100% free, no ads. Try ListLink today."
        />
        <meta
          name="keywords"
          content="free gift list app, secret santa organizer, sinterklaas app, expense tracking, shared lists, wish list, ListLink features"
        />
        <meta property="og:title" content="Free Gift List & Secret Santa App | ListLink" />
        <meta property="og:description" content="Organize wish lists, Secret Santa draws, Sinterklaas exchanges, and shared grocery lists — with expense tracking. 100% free." />
        <meta property="og:url" content="https://list-link.com/learn-more" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://list-link.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://list-link.com/learn-more" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* ── HERO ──────────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `
            radial-gradient(at 30% 50%, rgba(253,235,208,0.7) 0%, transparent 55%),
            radial-gradient(at 70% 20%, rgba(254,243,199,0.5) 0%, transparent 50%),
            #FDFAF5
          `,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          {/* Breadcrumb */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3, alignItems: 'center' }}>
            <Typography
              component={Link}
              to="/"
              sx={{
                fontSize: '0.8rem',
                color: '#8A7968',
                textDecoration: 'none',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 500,
                '&:hover': { color: '#E8891A' },
              }}
            >
              Home
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#C9C0B3' }}>›</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 500 }}>
              Features
            </Typography>
          </Box>

          <Typography
            variant="h1"
            component="h1"
            sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, mb: 2 }}
          >
            Everything ListLink Can Do
          </Typography>
          <Typography
            sx={{
              color: '#5C4F3D',
              fontSize: { xs: '1rem', md: '1.15rem' },
              lineHeight: 1.75,
              maxWidth: 540,
              mx: 'auto',
              mb: 4,
            }}
          >
            One free app for gift lists, group events, expense tracking, and everyday shared lists.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/register"
            sx={{ px: 4, py: 1.75 }}
          >
            Start For Free →
          </Button>
        </Container>
      </Box>

      {/* ── FEATURE DEEP DIVES (TABS) ─────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            overline="Feature Deep Dives"
            title="Everything You Need to Know"
          />

          <Paper
            sx={{
              borderRadius: 3,
              border: '1px solid #EDE8DF',
              boxShadow: '0 4px 24px rgba(26,20,16,0.07)',
              overflow: 'hidden',
              '&:hover': { transform: 'none', boxShadow: '0 4px 24px rgba(26,20,16,0.07)' },
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: '1px solid #EDE8DF',
                background: '#F7F2EA',
                px: 2,
                '& .MuiTab-root': {
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: '#8A7968',
                  textTransform: 'none',
                  minHeight: 56,
                  '&.Mui-selected': { color: '#E8891A' },
                },
                '& .MuiTabs-indicator': { background: '#E8891A', height: 3, borderRadius: 3 },
              }}
            >
              {tabs.map(({ label, icon }) => (
                <Tab key={label} label={label} icon={icon} iconPosition="start" />
              ))}
            </Tabs>

            <Box sx={{ p: { xs: 3, md: 5 } }}>
              {/* Tab 0: Gift Lists */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={5} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h3" sx={{ mb: 2.5 }}>Gift Lists & Wish Lists</Typography>
                    <Typography sx={{ color: '#5C4F3D', mb: 3, lineHeight: 1.75 }}>
                      Create wish lists for birthdays, Christmas, baby showers, or any occasion. Add
                      items from any online store with a link. Share with anyone via
                      a link — they can see what to buy without you ruining the surprise.
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mb: 4, color: '#5C4F3D', lineHeight: 2 }}>
                      <li>Add items from any online store with a link</li>
                      <li>Others can mark items as purchased (secretly)</li>
                      <li>No duplicate gifts — reservations are visible to buyers</li>
                      <li>Works for all occasions, all year round</li>
                    </Box>
                    <Button variant="contained" size="large" component={Link} to="/register" sx={{ px: 4 }}>
                      Create Your First List
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        background: 'rgba(232,137,26,0.05)',
                        borderRadius: 3,
                        p: 3,
                        border: '1px solid rgba(232,137,26,0.15)',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: '#1A1410', mb: 2, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        🎁 How it looks
                      </Typography>
                      {[
                        { name: 'Wireless headphones', price: '€89', status: 'reserved', buyer: 'Tom' },
                        { name: 'Cozy blanket set', price: '€45', status: 'available', buyer: null },
                        { name: 'Recipe book — Italian', price: '€28', status: 'available', buyer: null },
                        { name: 'Yoga mat', price: '€55', status: 'available', buyer: null },
                      ].map(({ name, price, status, buyer }) => (
                        <Box
                          key={name}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1.25,
                            borderBottom: '1px solid rgba(232,137,26,0.1)',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                              {name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#8A7968', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                              {price}
                            </Typography>
                          </Box>
                          <Chip
                            label={status === 'reserved' ? `Buying (${buyer})` : 'Available'}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              background: status === 'reserved' ? 'rgba(45,106,79,0.1)' : 'rgba(232,137,26,0.1)',
                              color: status === 'reserved' ? '#2D6A4F' : '#E8891A',
                              height: 22,
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Tab 1: Secret Santa */}
              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={5} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h3" sx={{ mb: 2.5 }}>Secret Santa & Group Events</Typography>
                    <Typography sx={{ color: '#5C4F3D', mb: 3, lineHeight: 1.75 }}>
                      Organize Secret Santa draws for offices, families, or friend groups.
                      Set a budget, draw names automatically, and let everyone add their wish
                      list items. Participants can mark gifts as secretly purchased so no
                      surprise is ruined.
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mb: 4, color: '#5C4F3D', lineHeight: 2 }}>
                      <li>Automatic name drawing — no self-matches</li>
                      <li>Integrated wish lists per participant</li>
                      <li>Works for offices, families, friend groups</li>
                      <li>Also supports White Elephant / Yankee Swap</li>
                    </Box>
                    <Button variant="contained" size="large" component={Link} to="/register" sx={{ px: 4 }}>
                      Start a Secret Santa
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        background: 'rgba(225,29,72,0.04)',
                        borderRadius: 3,
                        p: 3,
                        border: '1px solid rgba(225,29,72,0.12)',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: '#1A1410', mb: 2.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        🎅 Office Secret Santa 2026
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Budget per person</Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>€30</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Participants</Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>12 people</Typography>
                      </Box>
                      <Box
                        sx={{
                          background: '#FFFFFF',
                          borderRadius: 2,
                          p: 2,
                          border: '1px solid #EDE8DF',
                          textAlign: 'center',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.75rem', color: '#8A7968', fontFamily: '"Plus Jakarta Sans", sans-serif', mb: 0.5 }}>
                          You are buying for
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: '#E11D48', fontSize: '1.1rem', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                          🎁 Sarah M.
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#8A7968', mt: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                          Only you can see this
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Tab 2: Sinterklaas */}
              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={5} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Chip
                      label="The Only App Built for Sinterklaas"
                      size="small"
                      sx={{
                        background: 'rgba(234,88,12,0.1)',
                        color: '#EA580C',
                        fontWeight: 700,
                        mb: 2.5,
                      }}
                    />
                    <Typography variant="h3" sx={{ mb: 1 }}>
                      Sinterklaas 🇳🇱🇧🇪
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontStyle: 'italic',
                        color: '#EA580C',
                        fontSize: '1rem',
                        mb: 2.5,
                      }}
                    >
                      De enige app speciaal voor Sinterklaas.
                    </Typography>
                    <Typography sx={{ color: '#5C4F3D', mb: 3, lineHeight: 1.75 }}>
                      Organize your Surprise exchange digitally. Draw names, manage wish lists
                      (pietenwensen), and coordinate the December 5th celebration — all in one
                      place. No more paper slips, no more awkward email chains.
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mb: 4, color: '#5C4F3D', lineHeight: 2 }}>
                      <li>Dedicated Sinterklaas event type</li>
                      <li>Lootjes trekken — automated name drawing</li>
                      <li>Wish list sharing for all participants</li>
                      <li>Surprise marking — secretly claim a gift</li>
                      <li>Works for families, schools, workplaces</li>
                    </Box>
                    <Button variant="contained" size="large" component={Link} to="/register" sx={{ px: 4 }}>
                      Organize Your Sinterklaas
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box
                      component="img"
                      src={sinterklaasIllustration}
                      alt="Sinterklaas wooden shoe filled with gifts and pepernoten"
                      sx={{
                        width: '100%',
                        maxWidth: 420,
                        borderRadius: 3,
                        display: 'block',
                      }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Tab 3: Expense Tracking */}
              <TabPanel value={tabValue} index={3}>
                <Grid container spacing={5} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Chip
                      label="The Feature No Competitor Offers"
                      size="small"
                      sx={{ background: 'rgba(124,58,237,0.1)', color: '#7C3AED', fontWeight: 700, mb: 2.5 }}
                    />
                    <Typography variant="h3" sx={{ mb: 2.5 }}>Expense Tracking Dashboard</Typography>
                    <Typography sx={{ color: '#5C4F3D', mb: 3, lineHeight: 1.75 }}>
                      Paid for the Christmas dinner? Bought the Secret Santa gift for the group?
                      Record it in ListLink. The dashboard tracks what you've spent per person
                      and event. No spreadsheets, no awkward group chats.
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mb: 4, color: '#5C4F3D', lineHeight: 2 }}>
                      <li>Track purchases per person or event</li>
                      <li>Spending breakdown by recipient and occasion</li>
                      <li>See your purchase history at a glance</li>
                      <li>No other gift list app has this feature</li>
                    </Box>
                    <Button variant="contained" size="large" component={Link} to="/register" sx={{ px: 4 }}>
                      Try the Dashboard
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        background: 'rgba(124,58,237,0.04)',
                        borderRadius: 3,
                        p: 3,
                        border: '1px solid rgba(124,58,237,0.12)',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: '#1A1410', mb: 2.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        💜 Group Expense Summary
                      </Typography>
                      {[
                        { name: 'Emma (you)', paid: '€45.00', balance: '+€22.00', pos: true },
                        { name: 'Tom', paid: '€15.00', balance: '-€15.00', pos: false },
                        { name: 'Sara', paid: '€24.50', balance: '-€5.50', pos: false },
                        { name: 'Mike', paid: '€0.00', balance: '-€22.00', pos: false },
                      ].map(({ name, paid, balance, pos }) => (
                        <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25, borderBottom: '1px solid rgba(124,58,237,0.08)', '&:last-child': { borderBottom: 'none' } }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{name}</Typography>
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: '#8A7968', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{paid}</Typography>
                            <Chip
                              label={balance}
                              size="small"
                              sx={{
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                height: 22,
                                background: pos ? 'rgba(45,106,79,0.1)' : 'rgba(225,29,72,0.08)',
                                color: pos ? '#2D6A4F' : '#E11D48',
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Tab 4: General Lists */}
              <TabPanel value={tabValue} index={4}>
                <Grid container spacing={5} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h3" sx={{ mb: 2.5 }}>General Shared Lists</Typography>
                    <Typography sx={{ color: '#5C4F3D', mb: 3, lineHeight: 1.75 }}>
                      ListLink isn't just for gift-giving. Use it for your weekly grocery run,
                      packing list before a trip, or collaborative to-do list with a partner or
                      flatmate. One app — all your lists.
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mb: 4, color: '#5C4F3D', lineHeight: 2 }}>
                      <li>Grocery lists that sync across devices</li>
                      <li>Packing lists for trips</li>
                      <li>Shared to-do lists for home or work</li>
                      <li>Check off items as you go</li>
                      <li>Switch between list types in seconds</li>
                    </Box>
                    <Button variant="contained" size="large" component={Link} to="/register" sx={{ px: 4 }}>
                      Create a Shared List
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        background: 'rgba(14,165,233,0.04)',
                        borderRadius: 3,
                        p: 3,
                        border: '1px solid rgba(14,165,233,0.12)',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: '#1A1410', mb: 2.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        🛒 Weekly Groceries
                      </Typography>
                      {[
                        { item: 'Bread', done: true },
                        { item: 'Milk (2L)', done: true },
                        { item: 'Chicken breast', done: false },
                        { item: 'Pasta — penne', done: false },
                        { item: 'Cherry tomatoes', done: false },
                        { item: 'Parmesan', done: false },
                      ].map(({ item, done }) => (
                        <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: done ? '#2D6A4F' : 'transparent',
                              border: done ? 'none' : '1.5px solid #EDE8DF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {done && <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              color: done ? '#8A7968' : '#1A1410',
                              fontFamily: '"Plus Jakarta Sans", sans-serif',
                              fontWeight: done ? 400 : 500,
                              textDecoration: done ? 'line-through' : 'none',
                            }}
                          >
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* ── FEATURE GRID ─────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: '#F7F2EA' }}>
        <Container maxWidth="lg">
          <SectionHeading
            overline="All Features"
            title="Built for Everything"
            subtitle="Every feature in ListLink — completely free."
          />
          <Grid container spacing={2.5}>
            {featureGrid.map(({ icon: Icon, label, desc }) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <Paper
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: '16px',
                    border: '1px solid #EDE8DF',
                    boxShadow: '0 2px 8px rgba(26,20,16,0.05)',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(26,20,16,0.09)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'rgba(232,137,26,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: '#E8891A', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1A1410', mb: 0.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#5C4F3D', lineHeight: 1.5, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                      {desc}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── COMPARISON TABLE ─────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: '#FDFAF5' }}>
        <Container maxWidth="md">
          <SectionHeading
            overline="Comparison"
            title="How ListLink Stacks Up"
            subtitle="See how ListLink compares to the popular alternatives."
          />
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              border: '1px solid #EDE8DF',
              boxShadow: '0 4px 24px rgba(26,20,16,0.07)',
              '&:hover': { transform: 'none', boxShadow: '0 4px 24px rgba(26,20,16,0.07)' },
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1410', py: 2.5 }}>
                    Feature
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#E8891A', background: 'rgba(232,137,26,0.05)', py: 2.5 }}>
                    ListLink
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#8A7968', py: 2.5 }}>
                    Elfster
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#8A7968', py: 2.5 }}>
                    Amazon Lists
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonRows.map(({ feature, listlink, elfster, amazon }, i) => (
                  <TableRow
                    key={feature}
                    sx={{
                      background: i % 2 === 0 ? '#FFFFFF' : '#FDFAF5',
                      '&:last-child td': { border: 0 },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.88rem', color: '#1A1410', py: 2 }}>
                      {feature}
                    </TableCell>
                    <TableCell align="center" sx={{ background: 'rgba(232,137,26,0.03)', py: 2 }}>
                      <CompCell v={listlink} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      <CompCell v={elfster} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      <CompCell v={amazon} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: '#F7F2EA' }}>
        <Container maxWidth="md">
          <SectionHeading
            overline="FAQ"
            title="Common Questions"
          />
          {faqs.map(({ q, a }) => (
            <Accordion
              key={q}
              elevation={0}
              disableGutters
              sx={{
                mb: 1.5,
                borderRadius: '12px !important',
                border: '1px solid #EDE8DF',
                background: '#FFFFFF',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(26,20,16,0.07)' },
                transition: 'box-shadow 0.2s',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#E8891A' }} />}
                sx={{
                  px: 3,
                  py: 0.5,
                  '& .MuiAccordionSummary-content': { my: 1.5 },
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1A1410', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                  {q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                <Typography sx={{ color: '#5C4F3D', lineHeight: 1.75, fontSize: '0.9rem' }}>
                  {a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ── FINAL CTA ────────────────────────────────────────────────────────── */}
      <AmberCTA
        title="Ready to Try It?"
        sub="Free. No credit card. Ready in 60 seconds."
        btnLabel="Get Started Free →"
        to="/register"
      />
    </Box>
  );
}

export default LearnMorePage;
